import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, RadioGroup, Input, Alert, Tabs, useToast } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { toCurrencyCode } from '@/hooks/use-tenant-currency';
import { subscriptionService } from '@/services/subscription.service';
import type { SubscriptionPlan } from '@/types/subscription';
import { GLASS_PANEL, cn, planTone, toneClasses } from './design-tokens';
import { planPrice } from './plan-pricing';

type ActionMode = 'upgrade' | 'downgrade' | 'renew';

interface PlanActionsPanelProps {
  tenantId: string;
  currentPlanName?: string | null;
  expiresAt?: string | null;
  /** The tenant's currency: plans are priced in it, or in INR when a plan has no price in it. */
  tenantCurrency?: string | null;
  /** Disable the form if caller cannot act (MODERATOR, OWNER, etc.). */
  readOnly?: boolean;
}

const DURATION_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '1 year' },
];

export function PlanActionsPanel({
  tenantId,
  currentPlanName,
  expiresAt,
  tenantCurrency,
  readOnly = false,
}: PlanActionsPanelProps) {
  const currency = toCurrencyCode(tenantCurrency);
  const [mode, setMode] = useState<ActionMode>('upgrade');
  const [plan, setPlan] = useState<string>(currentPlanName ?? '');
  const [durationDays, setDurationDays] = useState<string>('365');
  const [notes, setNotes] = useState<string>('');
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ['subscription-plans', 'active'],
    queryFn: () => subscriptionService.listPlans(false),
    staleTime: 60_000,
  });

  const currentPlan = useMemo(
    () => plans.find((p) => p.name === currentPlanName),
    [plans, currentPlanName],
  );

  // Filter candidate plans per mode (exclude current plan for upgrade/downgrade,
  // lock plan for renew).
  const candidatePlans = useMemo<SubscriptionPlan[]>(() => {
    if (mode === 'renew') return currentPlan ? [currentPlan] : [];
    if (!currentPlan) return plans.filter((p) => p.name !== currentPlanName);
    if (mode === 'upgrade') {
      return plans.filter((p) => p.sort_order > currentPlan.sort_order);
    }
    return plans.filter((p) => p.sort_order < currentPlan.sort_order);
  }, [mode, plans, currentPlan, currentPlanName]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.name === plan),
    [plans, plan],
  );

  const upgradeMutation = useMutation({
    mutationFn: () =>
      subscriptionService.upgrade(tenantId, {
        plan,
        duration_days: Number(durationDays),
        notes: notes.trim() || undefined,
      }),
  });
  const downgradeMutation = useMutation({
    mutationFn: () =>
      subscriptionService.downgrade(tenantId, {
        plan,
        notes: notes.trim() || undefined,
      }),
  });
  const renewMutation = useMutation({
    mutationFn: () =>
      subscriptionService.renew(tenantId, {
        duration_days: Number(durationDays),
        notes: notes.trim() || undefined,
      }),
  });

  const activeMutation =
    mode === 'upgrade' ? upgradeMutation : mode === 'downgrade' ? downgradeMutation : renewMutation;

  const canSubmit =
    !readOnly &&
    !activeMutation.isPending &&
    (mode === 'renew' ? Boolean(currentPlan) : Boolean(plan) && plan !== currentPlanName);

  async function onSubmit() {
    try {
      if (mode === 'upgrade') await upgradeMutation.mutateAsync();
      else if (mode === 'downgrade') await downgradeMutation.mutateAsync();
      else await renewMutation.mutateAsync();
      toast({
        title:
          mode === 'upgrade'
            ? 'Subscription upgraded'
            : mode === 'downgrade'
              ? 'Downgrade scheduled'
              : 'Subscription renewed',
        variant: 'success',
      });
      setNotes('');
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Action failed';
      toast({ title: message, variant: 'error' });
    }
  }

  const cycle = Number(durationDays) >= 365 ? 'yearly' : 'monthly';
  function formatPrice(p: SubscriptionPlan, c: 'monthly' | 'yearly'): string {
    const price = planPrice(p, currency, c);
    return formatCurrency(price.amount, price.currency);
  }

  const palette = toneClasses(planTone(currentPlanName));
  const modeHelperText: Record<ActionMode, string> = {
    upgrade: 'Upgrades take effect immediately. An invoice is generated and the plan period resets.',
    downgrade:
      'Downgrades take effect at the end of the current billing period, so tenants keep what they paid for.',
    renew: 'Renewals extend the current plan by the selected duration. Expiry date is pushed forward.',
  };

  return (
    <div className={cn(GLASS_PANEL, 'p-6 transition-shadow duration-200 hover:shadow-lg')}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', palette.icon)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
              Plan actions
            </h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Upgrade, downgrade, or renew this tenant&rsquo;s subscription.
            </p>
          </div>
        </div>
        {readOnly && (
          <span className="rounded-full bg-neutral-100 dark:bg-neutral-800 px-2.5 py-1 text-[11px] font-medium text-neutral-600 dark:text-neutral-300">
            Read-only — requires ADMIN
          </span>
        )}
      </div>

      <Tabs
        variant="segmented"
        items={[
          { id: 'upgrade', label: 'Upgrade' },
          { id: 'downgrade', label: 'Downgrade' },
          { id: 'renew', label: 'Renew' },
        ]}
        activeId={mode}
        onChange={(id) => {
          setMode(id as ActionMode);
          if (id === 'renew' && currentPlanName) setPlan(currentPlanName);
          else setPlan('');
        }}
      />

      <div className="mt-5">
        <Alert variant="info" description={modeHelperText[mode]} />
      </div>

      {mode !== 'renew' && (
        <div className="mt-5">
          <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
            Target plan
          </label>
          {plansLoading ? (
            <p className="text-sm text-neutral-500">Loading plans…</p>
          ) : candidatePlans.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 italic">
              {mode === 'upgrade'
                ? 'No higher-tier plans available — this tenant is already on the top plan.'
                : 'No lower-tier plans available.'}
            </p>
          ) : (
            <RadioGroup
              variant="card"
              value={plan}
              onChange={setPlan}
              options={candidatePlans.map((p) => ({
                value: p.name,
                label: p.display_name,
                description: `${formatPrice(p, 'yearly')} / year · ${p.max_locations} locations · ${p.max_users} users`,
              }))}
            />
          )}
        </div>
      )}

      {mode !== 'downgrade' && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
              Duration
            </label>
            <RadioGroup
              variant="horizontal"
              value={durationDays}
              onChange={setDurationDays}
              options={DURATION_OPTIONS}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
              Amount preview
            </label>
            <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/60 px-4 py-3">
              <p className="text-xl font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
                {selectedPlan
                  ? formatPrice(selectedPlan, cycle)
                  : mode === 'renew' && currentPlan
                    ? formatPrice(currentPlan, cycle)
                    : '—'}
              </p>
              <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                {Number(durationDays) >= 365 ? 'Yearly' : 'Pro-rated monthly rate'}
                {expiresAt && mode === 'renew' && (
                  <span> · extends from {new Date(expiresAt).toLocaleDateString('en-IN')}</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-5">
        <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
          Internal notes (optional)
        </label>
        <Input
          value={notes}
          onChange={setNotes}
          placeholder="e.g. Enterprise onboarding — signed 3-year contract"
        />
      </div>

      <div className="mt-6 flex items-center justify-end gap-2">
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={!canSubmit}
          loading={activeMutation.isPending}
        >
          {mode === 'upgrade' && 'Upgrade now'}
          {mode === 'downgrade' && 'Schedule downgrade'}
          {mode === 'renew' && 'Renew subscription'}
        </Button>
      </div>
    </div>
  );
}
